import { t } from '@/utils/i18n'
import { useState, useMemo } from 'react'
import { useSetState } from 'ahooks'
import { NavBar, Input, Toast, DotLoading } from 'antd-mobile'
import { LeftOutline } from 'antd-mobile-icons'
import { useNavigate } from 'react-router-dom'
import { routesMap } from '@/utils'
import { useCorpInfoStore } from '@/stores'
import Cell from '@/components/Cell'
import Gap from '@/components/Gap'
import styles from './index.module.less'
import { apis, utils, common, image, constant, storageUtil } from '@/utils'
import DeliveryAddress from '@/components/DeliveryAddress'
import FileUpload from '@/components/FileUpload'
import { CustomerAddress } from '@/pages/Order/types'
import type { PfUser } from '../types'

const { getLocalStorage } = storageUtil
const { crmApi, ossApi } = apis
const { getFilePathName } = common
const { getAbsoluteUrl } = image
const { to } = utils
const {
  regExp: { PHONE_REG, EMAIL_REG },
} = constant

function MyEdit() {
  const { corpInfo, setCorpInfo } = useCorpInfoStore()
  const navigate = useNavigate()
  const { pfUserId, pfUser, corpId } = corpInfo
  const [form, setForm] = useSetState<PfUser>(pfUser || {})
  const [deliveryVisible, setDeliveryVisible] = useState(false)
  const [saveLoading, setSaveLoading] = useState(false)
  const addressLabel = useMemo(() => {
    const { area, address } = form
    if (!area?.length && !address) return ''
    return area?.map((item: any) => item.label)?.join('') + address
  }, [form])
  const zoneNumber = getLocalStorage('zoneNumber')

  async function onUpload(files: File[] = []) {
    const file = files[0]
    Toast.show({
      content: t('上传中'),
      duration: 0,
      icon: 'loading',
    })
    const fileFormData = new FormData()
    fileFormData.append('file', file as any)
    fileFormData.append('filePathName', getFilePathName())
    fileFormData.append('corpId', corpId)
    const [err, res] = await to(ossApi.uploadSingle(fileFormData))

    if (err || !res) {
      Toast.show(err?.msg || t('上传头像失败'))
      return
    }
    const resData = res.data || []
    const { relativePath } = resData[0]
    setForm({
      avatar: relativePath,
    })
    Toast.show({
      content: t('上传成功'),
      icon: 'success',
    })
  }

  async function onSave() {
    setSaveLoading(true)
    const [err, res] = await to(
      crmApi.updatePfUserDetail({
        id: pfUserId,
        ...form,
      }),
    )
    setSaveLoading(false)

    if (err || !res) {
      Toast.show(err?.msg || t('更新用户信息失败'))
      return
    }
  }

  async function onGoBack() {
    await onSave()
    setCorpInfo({
      ...corpInfo,
      pfUser: {
        ...pfUser,
        ...form,
      },
    })
    navigate(routesMap.MyCenter)
  }

  // 手机号码变化
  function onPhoneBlur() {
    if (!PHONE_REG.test(form.phone)) {
      Toast.show(t('请输入正确的手机号码'))
      setForm({ phone: '' })
    }
  }

  // 邮箱地址变化
  function onEmailBlur() {
    if (!EMAIL_REG.test(form.email)) {
      Toast.show(t('请输入正确的邮箱地址'))
      setForm({ email: '' })
    }
  }

  // 地址保存
  function onAddressSave(addressData: CustomerAddress) {
    setForm({
      ...form,
      area: addressData.area,
      address: addressData.address,
    })
    setDeliveryVisible(false)
  }

  const backArrowNode = saveLoading ? <DotLoading /> : <LeftOutline />

  return (
    <div className={styles['my-info-edit']}>
      <NavBar
        children={t('我的资料')}
        backArrow={backArrowNode}
        onBack={onGoBack}
      />
      <Gap height="1px" />
      <Cell
        padding="16px 24px"
        right={
          <FileUpload onChange={onUpload}>
            <div className={styles['change-avatar']}>{t('更换头像')}</div>
          </FileUpload>
        }
      >
        <div className={styles['form-item']}>
          <div className={styles['form-label']}>{t('头像')}</div>
          {form.avatar && (
            <div className={styles['form-content']}>
              <img
                className={styles['avatar']}
                src={getAbsoluteUrl(form.avatar)}
              />
            </div>
          )}
        </div>
      </Cell>
      <Gap height="1px" />
      <Cell padding="16px 24px" right={null}>
        <div className={styles['form-item']}>
          <div className={styles['form-label']}>{t('姓名')}</div>
          <Input
            className={styles['form-content']}
            value={form.name}
            maxLength={40}
            onChange={(val) => setForm({ name: val })}
          />
        </div>
      </Cell>
      <Gap height="1px" />
      <Cell padding="16px 24px" right={null}>
        <div className={styles['form-item']}>
          <div className={styles['form-label']}>{t('公司')}</div>
          <Input
            className={styles['form-content']}
            value={form.companyName}
            maxLength={40}
            onChange={(val) => setForm({ companyName: val })}
          />
        </div>
      </Cell>
      <Gap height="8px" />

      <Cell padding="16px 24px" right={null}>
        <div className={styles['form-item']}>
          <div className={styles['form-label']}>{t('手机')}</div>
          {zoneNumber && (
            <span className={styles['zone-number']}>{zoneNumber}</span>
          )}
          <Input
            className={styles['form-content']}
            value={form.phone}
            maxLength={18}
            onChange={(val) => setForm({ phone: val })}
            onBlur={onPhoneBlur}
          />
        </div>
      </Cell>
      <Gap height="1px" />
      <Cell padding="16px 24px" right={null}>
        <div className={styles['form-item']}>
          <div className={styles['form-label']}>{t('邮箱')}</div>
          <Input
            className={styles['form-content']}
            value={form.email}
            maxLength={18}
            onChange={(val) => setForm({ email: val })}
            onBlur={onEmailBlur}
          />
        </div>
      </Cell>
      <Gap height="8px" />
      <Gap height="1px" />
      <Cell padding="16px 24px" onClick={() => setDeliveryVisible(true)}>
        <div className={styles['form-item']}>
          <div className={styles['form-label']}>{t('我的地址')}</div>
          <div className={`${styles['form-content']} ep-3`}>{addressLabel}</div>
        </div>
      </Cell>
      <DeliveryAddress
        visible={deliveryVisible}
        pfUserId={pfUserId}
        initData={{
          name: form.name,
          phone: form.phone,
        }}
        onCancel={() => setDeliveryVisible(false)}
        onOk={onAddressSave}
      />
    </div>
  )
}

export default MyEdit
